import { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Divider from '@mui/material/Divider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import { ToolLayout } from '../ToolLayout';

const useStyles = makeStyles()((theme) => ({
  layout: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2), maxWidth: 720 },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  sliderGroup: { flex: 1, minWidth: 200 },
  sliderLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  resultPaper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
  },
  uuid: {
    fontFamily: '"Fira Code","Consolas",monospace',
    fontSize: 13,
  },
}));

export function UuidGeneratorPage() {
  const { classes } = useStyles();
  const [count, setCount] = useState(5);
  const [uuids, setUuids] = useState<string[]>([]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const generate = useCallback(() => {
    const generated: string[] = [];
    for (let i = 0; i < count; i++) {
      generated.push(crypto.randomUUID());
    }
    setUuids(generated);
  }, [count]);

  const copyAll = useCallback(() => {
    navigator.clipboard.writeText(uuids.join('\n'));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  }, [uuids]);

  const copyOne = (uuid: string, idx: number) => {
    navigator.clipboard.writeText(uuid);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(i => i === idx ? null : i), 2000);
  };

  return (
    <ToolLayout
      title="UUID Generator"
      subtitle="Generate RFC 4122 v4 UUIDs"
    >
      <Box className={classes.layout}>
        <Box className={classes.controls}>
          <Box className={classes.sliderGroup}>
            <Typography className={classes.sliderLabel}>
              Count: <strong>{count}</strong>
            </Typography>
            <Slider
              value={count}
              min={1}
              max={50}
              step={1}
              onChange={(_, v) => setCount(v as number)}
              marks={[
                { value: 1, label: '1' },
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
              ]}
              color="primary"
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={generate}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Generate
          </Button>
        </Box>

        {uuids.length > 0 && (
          <Paper className={classes.resultPaper} elevation={0}>
            <Box className={classes.listHeader}>
              <Typography variant="body2" color="textSecondary">
                {uuids.length} UUID{uuids.length !== 1 ? 's' : ''} generated
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={copyAll}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  {copiedAll ? 'Copied!' : 'Copy All'}
                </Button>
                <Tooltip title="Clear">
                  <IconButton size="small" onClick={() => setUuids([])}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <List dense disablePadding>
              {uuids.map((uuid, i) => (
                <Box key={uuid}>
                  {i > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={<span className={classes.uuid}>{uuid}</span>}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={copiedIdx === i ? 'Copied!' : 'Copy'}>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => copyOne(uuid, i)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ToolLayout>
  );
}
